import React from 'react';
import type { ValidIconName } from './Icon';
import { Icon } from './Icon';

/**
 * Props for {@link MenuItem}
 */
export type MenuItemProps = {
  label: string | React.JSX.Element;
  className?: string;
  onClick: () => void;
  disabled: boolean;
  active: boolean;
  ariaLabel?: string;
  icon?: ValidIconName;
  checkbox?: boolean;
  verticalAlignment?: boolean;
};

/**
 * MenuItem component representing the SHUI component of the same name.
 * https://shui.siemens-healthineers.com/styleguide/components/menu-item
 */
export function MenuItem({
  label,
  className,
  onClick,
  disabled,
  active,
  ariaLabel,
  icon,
  verticalAlignment = false,
  checkbox = false,
  ...props
}: Readonly<MenuItemProps>): React.JSX.Element {
  const classes = ['ehs-menu-item'];
  if (disabled) classes.push('disabled');
  if (className) classes.push(className);
  if (active) classes.push('active');
  if (verticalAlignment) classes.push('vertical-alignment');
  const onClickHandler = () => {
    if (!disabled) {
      onClick();
    }
  };

  return (
    <button
      {...props}
      className={classes.join(' ')}
      onClick={onClickHandler}
      aria-label={ariaLabel ?? undefined}
      type="button"
      aria-expanded={active}
      disabled={disabled}
      aria-disabled={disabled ? true : undefined}
      aria-checked={checkbox ? active : undefined}
    >
      {checkbox && (
        <Icon
          className="checkbox-icon"
          aria-label={undefined}
          iconName={
            active
              ? 'materialicon-check_box'
              : 'materialicon-check_box_outline_blank'
          }
        />
      )}
      {!checkbox && icon && <Icon iconName={icon} aria-label={undefined} />}
      {label}
    </button>
  );
}
